/**
 * A/B Testing Framework Core Utilities (Server-Side)
 *
 * This module contains the core utility functions for server-side A/B testing:
 * - Deterministic cohort assignment using djb2 hashing
 * - URL override parsing for server requests
 */

import type { DeviceType } from '@travel/traveler-core/hooks/useDeviceType';

import type { ExperimentAssignment, ExperimentConfig } from 'store/experiments/types';

/**
 * djb2 hash function for cohort assignment
 * Produces uniform distribution for A/B testing
 * @param {string} str - The string to hash
 * @returns {number} A number between 0 and 99
 */
export function getHashCohort(str: string): number {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    // Bitwise left shift 5, add hash, and add the character code
    hash = (hash << 5) + hash + str.charCodeAt(i);
  }
  // Ensure the result is a positive number and map it to a 0-99 range
  return Math.abs(hash) % 100;
}

/**
 * Assigns a user to a cohort based on experiment configuration for a given platform
 * @param {string} userId - User's UUID
 * @param {string} experimentKey - Experiment identifier
 * @param {string} environment - Current environment (development, production)
 * @param {ExperimentConfig} experiment - Experiment configuration
 * @param {DeviceType} platform - Requesting device platform (pc, tl, sp). Used only
 * to select which platform's cohort/weight table to read
 * @returns {string | null} The assigned cohort name, or null if the experiment has no
 * cohorts configured for this platform (i.e. the platform is excluded from the test)
 */
export function assignUserToCohort(
  userId: string,
  experimentKey: string,
  environment: string,
  experiment: ExperimentConfig,
  platform: DeviceType,
): string | null {
  const cohorts = experiment.platformCohorts[platform];
  if (!cohorts || cohorts.length === 0) {
    return null;
  }

  const hashInput = userId + environment + experimentKey;
  const bucket = getHashCohort(hashInput);

  let cumulativeWeight = 0;
  for (const cohort of cohorts) {
    cumulativeWeight += cohort.weight;
    if (bucket < cumulativeWeight) {
      return cohort.name;
    }
  }

  // Fallback to control if something goes wrong
  return 'control';
}

/**
 * Parses URL override parameters from server request
 * @param {string} url - Full request URL
 * @returns {ExperimentAssignment} Override assignments from URL
 */
export function parseServerUrlOverrides(url: string): ExperimentAssignment {
  const overrides: ExperimentAssignment = {};

  try {
    const parsedUrl = new URL(url, 'http://localhost'); // Base URL needed for relative URLs
    const overrideParams = parsedUrl.searchParams.getAll('ab_override');

    overrideParams.forEach((override) => {
      const [experimentKey, cohort] = override.split(':');
      if (experimentKey && cohort) {
        overrides[experimentKey] = cohort;
      }
    });
  } catch {}

  return overrides;
}

/**
 * Formats experiment assignments for RAT Analytics
 * Converts assignments object to pipe-delimited string format: "ExperimentKey1:CohortName1|ExperimentKey2:CohortName2"
 * @param assignments - Object with experiment keys and assigned cohorts
 * @param experimentKeys - Optional array to filter specific experiments
 * @returns Formatted string for analytics or empty string if no valid assignments
 */
export function formatAssignmentsForAnalytics(
  assignments: ExperimentAssignment,
  experimentKeys?: string[],
): string {
  if (!assignments || typeof assignments !== 'object') {
    return '';
  }

  const filteredAssignments = experimentKeys
    ? Object.fromEntries(
        Object.entries(assignments).filter(([key]) => experimentKeys.includes(key)),
      )
    : assignments;

  const formattedPairs = Object.entries(filteredAssignments)
    .filter(([key, value]) => key && value)
    .map(([key, value]) => `${key}:${value}`)
    .join('|');

  return formattedPairs;
}

/**
 * Validates that analytics string doesn't exceed character limits
 * RAT Analytics has character limits for tracking parameters
 * @param analyticsString - The formatted analytics string
 * @param maxLength - Maximum allowed length (default: 255)
 * @returns true if within limits, false otherwise
 */
export function validateAnalyticsStringLength(
  analyticsString: string,
  maxLength: number = 255,
): boolean {
  return analyticsString.length <= maxLength;
}

/**
 * Safe version that truncates if needed and logs warnings
 * @param assignments - Experiment assignments object
 * @param experimentKeys - Optional array to filter specific experiments
 * @param maxLength - Maximum allowed length (default: 255)
 * @returns Formatted and validated string for analytics
 */
export function safeFormatAssignmentsForAnalytics(
  assignments: ExperimentAssignment,
  experimentKeys?: string[],
  maxLength: number = 255,
): string {
  const formatted = formatAssignmentsForAnalytics(assignments, experimentKeys);

  if (!validateAnalyticsStringLength(formatted, maxLength)) {
    console.warn(
      `A/B test analytics string too long (${formatted.length}/${maxLength}): ${formatted}`,
    );
    return formatted.substring(0, maxLength);
  }

  return formatted;
}
